import React, { useEffect } from 'react';
import {
  Box,
  Link,
  MUIDataTable,
  MUIDataTableColumn,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  Typography,
  useDataTable,
} from 'web-library';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';

import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { candidateService } from '../../../api';
import { useStyles } from '../../examRegistration/components/ExamRegistrationsListing';
import RouteKey from '../../../routes/key';
import {
  ActionType,
  CandidateRegisteredExamListingExamLevel,
  CandidateTransactionHistoryListingResponse,
} from '../../../api/candidate/exam/model';

const { examRegistrations, transactionHistoryRegistration } = RouteKey.exam.child;
const { amendment, exam } = RouteKey.child;
const { transactionHistoryAmendment } = RouteKey.amendment.child;
const { transactionHistorySummary, nonNationalTransactionHistory } = RouteKey.withdrawal.child;

enum NonNationalExamType {
  AEIS = 'AEIS',
  S_AEIS = 'S_AEIS',
  SPERS_SEC = 'SPERS_SEC',
  SPE = 'SPE',
}

const viewTransactionRouting = (
  tableData: CandidateTransactionHistoryListingResponse,
  dataIndex: number,
  tableHistory: RouteComponentProps['history']
) => {
  if (!(tableData.content[dataIndex].examLevels[0].examLevel in NonNationalExamType)) {
    tableHistory.push(
      `/${exam}/${examRegistrations.route}/${tableData.content[dataIndex].applicationUuid}/${transactionHistoryRegistration.route}`
    );
  }
  if (
    tableData.content[dataIndex].examLevels[0].examLevel === NonNationalExamType.AEIS ||
    tableData.content[dataIndex].examLevels[0].examLevel === NonNationalExamType.S_AEIS
  ) {
    tableHistory.push(
      `/${exam}/${examRegistrations.route}/${tableData.content[dataIndex].applicationUuid}/aeis/${nonNationalTransactionHistory.route}`
    );
  }
  if (
    tableData.content[dataIndex].examLevels[0].examLevel === NonNationalExamType.SPERS_SEC ||
    tableData.content[dataIndex].examLevels[0].examLevel === NonNationalExamType.SPE
  ) {
    tableHistory.push(
      `/${exam}/${examRegistrations.route}/${tableData.content[dataIndex].applicationUuid}/spers/${nonNationalTransactionHistory.route}`
    );
  }
};

const displayExamLevel = (examLevelObject: CandidateRegisteredExamListingExamLevel) => {
  if (examLevelObject.examLevel === NonNationalExamType.S_AEIS) {
    return 'S-AEIS';
  }
  if (examLevelObject.examLevel === NonNationalExamType.SPERS_SEC) {
    return 'SPERS-Sec';
  }
  return examLevelObject.examLevel;
};

const ApplicationId = (applicationId: string) => <Typography>{applicationId}</Typography>;

const ExamLevel = ({
  dataIndex,
  tableData,
  tableClasses,
}: {
  dataIndex: number;
  tableData: CandidateTransactionHistoryListingResponse;
  tableClasses: Record<string,string>;
}) => (
  <>
    {tableData.content[dataIndex].examLevels.map((examLevelObject) => (
      <Typography className={tableClasses.examLevelCol} key={dataIndex}>
        {displayExamLevel(examLevelObject)}
      </Typography>
    ))}
  </>
);

const ApplicationDateTime = ({
  dataIndex,
  tableData,
}: {
  dataIndex: number;
  tableData: CandidateTransactionHistoryListingResponse;
}) => (
  <Typography>
    {stringToLongDateAndShortTime(tableData.content[dataIndex].applicationDateTime)}
  </Typography>
);

const ActionTypeColumn = ({
  dataIndex,
  tableData,
}: {
  dataIndex: number;
  tableData: CandidateTransactionHistoryListingResponse;
}) => <Typography>{tableData.content[dataIndex].actionType}</Typography>;

const VIEW_TRANSACTION = (t: TFunction<'translation'>) =>
  t('amendment->>TransactionHistory.View Transaction');

const ActionsColumn = ({
  dataIndex,
  tableData,
  tableClasses,
  tableHistory,
}: {
  dataIndex: number;
  tableData: CandidateTransactionHistoryListingResponse;
  tableClasses: Record<string,string>;
  tableHistory: RouteComponentProps['history'];
}) => {
  const { actionType } = tableData.content[dataIndex];
  const { t } = useTranslation();

  if (
    actionType === ActionType.EXAMINATION_REGISTRATION ||
    actionType === ActionType.SUPPLEMENTARY_REGISTRATION
  ) {
    return (
      <Link
        id={`button-viewTransaction-${dataIndex}`}
        onClick={() => {
          viewTransactionRouting(tableData, dataIndex, tableHistory);
        }}
      >
        <Typography variant="h6" className={tableClasses.viewTransactionLink}>
          {VIEW_TRANSACTION(t)}
        </Typography>
      </Link>
    );
  }
  if (actionType === ActionType.EXAMINATION_WITHDRAWAL) {
    return (
      <Link
        onClick={() =>
          tableHistory.push(
            `/${exam}/${examRegistrations.route}/${tableData.content[dataIndex].applicationUuid}/${transactionHistorySummary.route}`
          )
        }
      >
        <Typography variant="h6" className={tableClasses.viewTransactionLink}>
          {VIEW_TRANSACTION(t)}
        </Typography>
      </Link>
    );
  }
  return (
    <Link
      id={`button-viewTransaction-${dataIndex}`}
      onClick={() =>
        tableHistory.push(
          `/${amendment}/${examRegistrations.route}/${tableData.content[dataIndex].applicationUuid}/${transactionHistoryAmendment.route}`
        )
      }
    >
      <Typography variant="h6" className={tableClasses.viewTransactionLink}>
        {VIEW_TRANSACTION(t)}
      </Typography>
    </Link>
  );
};

const ApplicationDateTimeLabel = () => (
  <Typography id="button-applicationDateTime">Application Date/Time</Typography>
);

const TransactionHistory = () => {
  const { state, dispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'applicationDateTime',
    order: 'desc',
  });

  const history = useHistory<any>();
  const { classes } = useStyles();
  const {
    data,
    refetch: getTransactionHistoryListing,
  } = candidateService.exam.candidate.useGetTransactionHistoryListing({
    order: state.order,
    sort: state.sortColumn,
  });

  const dataColumns = (
    tableData: CandidateTransactionHistoryListingResponse,
    tableClasses: Record<string,string>,
    tableHistory: RouteComponentProps['history']
  ): MUIDataTableColumn[] => [
    {
      name: 'applicationID',
      label: 'Application ID',
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: number) =>
          ApplicationId(tableData.content[dataIndex].applicationID),
      },
    },
    {
      name: 'examLevels',
      label: 'Exam Level(s)',
      options: {
        sort: true,
        setCellProps: () => ({ style: { minWidth: '230px', maxWidth: '230px' } }),
        customBodyRenderLite: (dataIndex: number) =>
          ExamLevel({
            dataIndex,
            tableData,
            tableClasses,
          }),
      },
    },
    {
      name: 'applicationDateTime',
      options: {
        sort: true,
        sortDirection: 'desc',
        customBodyRenderLite: (dataIndex: number) =>
          ApplicationDateTime({
            dataIndex,
            tableData,
          }),
        customHeadLabelRender: () => ApplicationDateTimeLabel(),
      },
    },
    {
      name: 'actionType',
      label: 'Action Type',
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: number) => ActionTypeColumn({ dataIndex, tableData }),
      },
    },
    {
      name: 'action',
      label: 'Action(s)',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) =>
          ActionsColumn({ dataIndex, tableData, tableClasses, tableHistory }),
      },
    },
  ];

  useEffect(() => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: data?.content,
        count: data?.content.length,
      },
    } as TableActionTypes);
  }, [dispatch, data?.content]);

  useEffect(() => {
    getTransactionHistoryListing().then(() => null);
  }, [getTransactionHistoryListing, state.sortColumn, state.order]);

  return (
    <>
      {data && (
        <>
          <Box id="transaction-history">
            <MUIDataTable
              state={state}
              dispatch={dispatch}
              columns={dataColumns(data, classes, history)}
              data={data.content}
              options={{
                ...tOptions,
                sort: true,
              }}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default TransactionHistory;
